import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackGameComplete, trackGameRestart } from '../utils/analytics';
import './GameStyles.css';

const SIZE = 16;
const START_SNAKE = [
  { x: 7, y: 8 },
  { x: 6, y: 8 },
  { x: 5, y: 8 },
];
const START_DIRECTION = { x: 1, y: 0 };
const BEST_KEY = 'ideapetal_snake_best';

const cellKey = (cell) => `${cell.x}-${cell.y}`;

const createFood = (snake) => {
  const blocked = new Set(snake.map(cellKey));
  const open = [];

  for (let y = 0; y < SIZE; y += 1) {
    for (let x = 0; x < SIZE; x += 1) {
      if (!blocked.has(`${x}-${y}`)) {
        open.push({ x, y });
      }
    }
  }

  return open[Math.floor(Math.random() * open.length)] || { x: 0, y: 0 };
};

const SnakeGarden = ({ game }) => {
  const [snake, setSnake] = useState(START_SNAKE);
  const [food, setFood] = useState(() => createFood(START_SNAKE));
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem(BEST_KEY) || 0));
  const [paused, setPaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const directionRef = useRef(START_DIRECTION);
  const nextDirectionRef = useRef(START_DIRECTION);

  const setDirection = useCallback((direction) => {
    const current = directionRef.current;
    if (current.x + direction.x === 0 && current.y + direction.y === 0) {
      return;
    }
    nextDirectionRef.current = direction;
  }, []);

  const restart = useCallback(() => {
    directionRef.current = START_DIRECTION;
    nextDirectionRef.current = START_DIRECTION;
    setSnake(START_SNAKE);
    setFood(createFood(START_SNAKE));
    setScore(0);
    setPaused(false);
    setGameOver(false);
    trackGameRestart(game);
  }, [game]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const keys = {
        ArrowUp: { x: 0, y: -1 },
        w: { x: 0, y: -1 },
        W: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 },
        s: { x: 0, y: 1 },
        S: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 },
        a: { x: -1, y: 0 },
        A: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 },
        d: { x: 1, y: 0 },
        D: { x: 1, y: 0 },
      };

      if (keys[event.key]) {
        event.preventDefault();
        setDirection(keys[event.key]);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setDirection]);

  useEffect(() => {
    if (paused || gameOver) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSnake((currentSnake) => {
        directionRef.current = nextDirectionRef.current;
        const head = currentSnake[0];
        const nextHead = {
          x: head.x + directionRef.current.x,
          y: head.y + directionRef.current.y,
        };

        const hitWall = nextHead.x < 0 || nextHead.x >= SIZE || nextHead.y < 0 || nextHead.y >= SIZE;
        const hitSnake = currentSnake.some((cell) => cell.x === nextHead.x && cell.y === nextHead.y);

        if (hitWall || hitSnake) {
          setGameOver(true);
          trackGameComplete(game, { result: 'game_over', score });
          return currentSnake;
        }

        const ateFood = nextHead.x === food.x && nextHead.y === food.y;
        const nextSnake = [nextHead, ...currentSnake];

        if (!ateFood) {
          nextSnake.pop();
          return nextSnake;
        }

        const nextScore = score + 10;
        setScore(nextScore);
        if (nextScore > best) {
          setBest(nextScore);
          localStorage.setItem(BEST_KEY, String(nextScore));
        }
        setFood(createFood(nextSnake));
        return nextSnake;
      });
    }, 140);

    return () => clearInterval(timer);
  }, [best, food, game, gameOver, paused, score]);

  const snakeCells = useMemo(() => new Set(snake.map(cellKey)), [snake]);
  const headKey = cellKey(snake[0]);

  return (
    <div className="playable-game">
      <div className="game-toolbar">
        <div className="game-stats">
          <span className="stat-pill">Score {score}</span>
          <span className="stat-pill">Best {best}</span>
        </div>
        <div className="game-actions">
          <button className="game-action-button secondary" onClick={() => setPaused((value) => !value)} type="button">
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button className="game-action-button" onClick={restart} type="button">
            Restart
          </button>
        </div>
      </div>

      {gameOver && <div className="game-status">Game over. Restart to grow again.</div>}

      <div className="snake-board" aria-label="Snake Garden board">
        {Array.from({ length: SIZE * SIZE }, (_, index) => {
          const x = index % SIZE;
          const y = Math.floor(index / SIZE);
          const key = `${x}-${y}`;
          const classes = ['snake-cell'];
          if (snakeCells.has(key)) classes.push('snake');
          if (headKey === key) classes.push('head');
          if (food.x === x && food.y === y) classes.push('food');
          return <div className={classes.join(' ')} key={key} />;
        })}
      </div>

      <div className="touch-pad" aria-label="Touch controls">
        <button className="up" onClick={() => setDirection({ x: 0, y: -1 })} type="button">Up</button>
        <button className="left" onClick={() => setDirection({ x: -1, y: 0 })} type="button">Left</button>
        <button className="down" onClick={() => setDirection({ x: 0, y: 1 })} type="button">Down</button>
        <button className="right" onClick={() => setDirection({ x: 1, y: 0 })} type="button">Right</button>
      </div>
    </div>
  );
};

export default SnakeGarden;
